import { takeLatest, put, call, delay } from "redux-saga/effects";
import { createSlice } from "@reduxjs/toolkit";
import * as XLSX from "xlsx";

import Api from "../utils/api";
import {
  HEIGHT_FOR_AGE_OPTIONS,
  WEIGHT_FOR_HEIGHT_OPTIONS,
} from "../utils/nutrition";

function* updateNutritionSaga({ payload }) {
  try {
    yield call(Api.post, "/nutrition/updateNutritionStatus", payload);
    yield put(setIsComplete(true));
    yield delay(1500);
    yield put(put(setIsComplete(false)));
    window.location.href = "/nutrition";
  } catch (err) {
    console.log(err);
  }
}

function* fetchNutritionsSaga() {
  try {
    const response = yield call(Api.get, `/nutrition/nutritionStatues`);
    yield put(setNutritions(response.data.nutritionData));
    yield put(setNutritionRule(response.data.nutritionRule));
  } catch (err) {
    console.log(err);
  }
}

function* fetchStudentsSaga({ payload: { classNo, roomNo, step } }) {
  try {
    const response = yield call(
      Api.get,
      `/nutrition/fetchStudentNutritionStatuses?classNo=${classNo}&roomNo=${roomNo}&step=${step}`
    );
    yield put(setStudents(response.data.result));
  } catch (err) {
    console.error(err);
  }
}

function* getStudentNutritionSaga({ payload: { userId, step } }) {
  try {
    const response = yield call(
      Api.get,
      `/nutrition/studentNutritionStatus?userId=${userId}&step=${step}`
    );
    yield put(setStudent(response.data.result));
    yield put(setNutritionRule(response.data.nutritionRule));
  } catch (err) {
    console.log(err);
  }
}

function* updateStudentNutritionSaga({
  payload: { values = {}, onSuccess = () => {} },
}) {
  try {
    yield call(Api.post, "/nutrition/updateStudentNutritionStatus", values);
    yield put(setIsComplete(true));
    yield delay(1500);
    yield put(setIsComplete(false));
    onSuccess();
  } catch (err) {
    console.log(err);
  }
}

function* downloadExcelStudentNutritionSaga({ payload: { data, columns } }) {
  try {
    const sheetData = [];
    const header = columns.reduce((memo, cur) => {
      if (cur.title) return [...memo, cur.title];
      return memo;
    }, []);

    sheetData.push(header);

    data.forEach((d) => {
      sheetData.push([
        d.name,
        d.weight,
        d.height,
        HEIGHT_FOR_AGE_OPTIONS.find((h) => h.value === d.heightForAge)?.label,
        WEIGHT_FOR_HEIGHT_OPTIONS.find((w) => w.value === d.weightForHeight)
          ?.label,
      ]);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(
      workbook,
      `Report_Nutrition#${new Date().toDateString()}.xlsx`
    );
  } catch (err) {
    console.error(err);
  }
}

export function* watchNutritionSaga() {
  yield takeLatest(onUpdateNutrition.type, updateNutritionSaga);
  yield takeLatest(fetchNutritions.type, fetchNutritionsSaga);
  yield takeLatest(fetchStudents.type, fetchStudentsSaga);
  yield takeLatest(getStudent.type, getStudentNutritionSaga);
  yield takeLatest(updateStudentNutrition.type, updateStudentNutritionSaga);
  yield takeLatest(
    downloadExcelStudentNutrition.type,
    downloadExcelStudentNutritionSaga
  );
}

export const nutritionSlice = createSlice({
  name: "nutrition",
  initialState: {
    isCompleted: false,
    nutritions: [],
    students: [],
    student: {},
    rule: {
      1: null,
      2: null,
    },
  },
  reducers: {
    setIsComplete: (state, action) => {
      state.isCompleted = action.payload;
    },
    onUpdateNutrition: () => {},
    fetchNutritions: () => {},
    setNutritions: (state, action) => {
      state.nutritions = action.payload;
    },
    setNutritionRule: (state, action) => {
      state.rule = {
        1: new Date(action.payload.startDate),
        2: new Date(action.payload.halfMatchDate),
      };
    },
    fetchStudents: () => {},
    setStudents: (state, action) => {
      state.students = action.payload;
    },
    getStudent: () => {},
    setStudent: (state, action) => {
      state.student = action.payload;
    },
    updateStudentNutrition: () => {},
    downloadExcelStudentNutrition: () => {},
  },
});

export const {
  setIsComplete,
  onUpdateNutrition,
  fetchNutritions,
  setNutritions,
  setNutritionRule,
  setStudents,
  fetchStudents,
  getStudent,
  setStudent,
  updateStudentNutrition,
  downloadExcelStudentNutrition,
} = nutritionSlice.actions;

export default nutritionSlice.reducer;
